import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider } from "antd";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { bubakiiTheme } from "./theme/bubakiiTheme";
import { muiTheme } from "./theme/muiTheme";
import { GlobalStyles } from "./styles/GlobalStyles";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SentryProvider } from "./contexts/SentryContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import ErrorBoundary from "./components/ErrorBoundary";
import UpdateToast from "./components/UpdateToast";
import InstallAppBanner from "./components/InstallAppBanner";
import ProtectedRoute, { RequireSuperAdmin } from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage, { DashboardProvider } from "./features/dashboard";
import OrdersPage from "./pages/Orders/OrdersPage";
import OrderDetailsView from "./features/orders/views/OrderDetailsView";
import OrderEditView from "./features/orders/views/OrderEditView";
import IncidentsView from "./features/quality/views/IncidentsView";
import IncidentDetailsView from "./features/quality/views/IncidentDetailsView";
import OrderCompensationView from "./features/compensation/views/OrderCompensationView.jsx";
import DueCompensationView from "./features/compensation/views/DueCompensationView.jsx";
import TripDashboard from "./features/trips/views/TripDashboard";
import TripDetailsView from "./features/trips/views/TripDetailsView";
import DropDetailsView from "./features/drops/views/DropDetailsView";
import DriversView from "./features/drivers/views/DriversView";
import VehiclesView from "./features/vehicles/views/VehiclesView";
import PartnersView from "./features/partners/views/PartnersView";
import PartnerDetailView from "./features/partners/views/PartnerDetailView";
import OpportunitiesView from "./features/opportunities/views/OpportunitiesView.jsx";
import OpportunityDetailView from "./features/opportunities/views/OpportunityDetailView.jsx";
import ProductsPage from "./pages/ProductsPage";
import OperationsLayout from "./features/operations/views/OperationsLayout";
import RouteCorridorsView from "./features/routeCorridors/views/RouteCorridorsView";
import TrustProfileTestPage from "./pages/TrustProfileTestPage";
// Messages/chat functionality disabled - WhatsApp chatbot paused
// import Messages from "./pages/Messages";
import Settings from "./pages/Settings";
import InventoryCataloguePage from "./pages/InventoryCataloguePage";
import PricingRateCardsPage from "./pages/PricingRateCardsPage";
import SystemHealthView from "./features/systemHealth/views/SystemHealthView.jsx";
import TermsManagementView from "./features/termsManagement/views/TermsManagementView";
import Leads from "./pages/Leads";
import Quotes from "./pages/Quotes";
import LeadDetailsView from "./features/leads/views/LeadDetailsView";
import ConversationsView from "./features/leads/views/ConversationsView";
import ManualLeadForm from "./features/leads/components/ManualLeadForm";
import AiLeadIntakeView from "./features/leads/intake/AiLeadIntakeView";
import QuoteForm from "./features/quotes/components/QuoteForm";
import QuoteDetailsView from "./features/quotes/views/QuoteDetailsView";
import QuoteEditView from "./features/quotes/views/QuoteEditView";
import BookingDetailsView from "./features/bookings/views/BookingDetailsView";
import InvoicesView from "./features/invoices/views/InvoicesView";
import InvoiceDetailsView from "./features/invoices/components/InvoiceDetailsView";
import InvoiceForm from "./features/invoices/components/InvoiceForm";
import InvoiceLedgerView from "./features/ledger/views/InvoiceLedgerView";
import JobLedgerView from "./features/ledger/views/JobLedgerView";
import ReconciliationQueueView from "./features/bankTransactions/views/ReconciliationQueueView";
import FinanceHubView from "./features/finance/views/FinanceHubView.jsx";
import LedgerReconciliationAuditView from "./features/finance/views/LedgerReconciliationAuditView.jsx";
import PayoutDashboardView from "./features/payouts/views/PayoutDashboardView.jsx";
import PayoutHistoryView from "./features/payouts/views/PayoutHistoryView.jsx";
import PayoutDetailView from "./features/payouts/views/PayoutDetailView.jsx";
import FollowUpsPage from "./features/followups/views/FollowUpsPage";
import FollowUpCreatePage from "./features/followups/views/FollowUpCreatePage";
import FollowUpDetailView from "./features/followups/views/FollowUpDetailView";
import FollowUpEditPage from "./features/followups/views/FollowUpEditPage";
import FeedbackPage from "./features/feedback/FeedbackPage";
import Notifications from "./pages/Notifications";
import AgentPage from "./features/agent/AgentPage";
import UsersPage from "./pages/UsersPage";
import CampaignUrlGeneratorPage from "./features/campaignUrlGenerator/CampaignUrlGeneratorPage.jsx";
import DataCopilotPage from "./features/dataCopilot";
import EditLead from "./pages/EditLead";
import PreConfirmationEdit from "./pages/PreConfirmationEdit";
import CustomersPage from "./pages/CustomersPage";
import CustomerDetailView from "./features/customers/components/CustomerDetailView";
import CallQueuePage from "./features/callRequests/pages/CallQueuePage";
import CallBookingPage from "./features/callRequests/pages/CallBookingPage";
import QuoteLinkPage from "./pages/QuoteLinkPage";
import PaymentInstructionsPage from "./pages/PaymentInstructionsPage";
import { QuoteLinkProvider } from "./contexts/QuoteLinkContext";
import { ToastProvider } from "./shared/toast";
// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Root redirect component - redirects to login or dashboard based on auth status
// Must be used inside AuthProvider
const RootRedirect = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
};

const LegacyJobLedgerRedirect = () => {
  const { jobId } = useParams();
  const safeJobId = encodeURIComponent(jobId || "");
  return <Navigate to={`/finance/jobs/${safeJobId}/ledger`} replace />;
};

const PartnerDetailRedirect = () => {
  const { partnerId } = useParams();
  const safePartnerId = encodeURIComponent(partnerId || "");
  return <Navigate to={`/operations/partners/${safePartnerId}`} replace />;
};

function App() {
  return (
    <ErrorBoundary name="App" showDetails={import.meta.env.DEV}>
      <ConfigProvider theme={bubakiiTheme}>
        <ThemeProvider theme={muiTheme}>
          <CssBaseline />
          <GlobalStyles />
          <UpdateToast />
          <InstallAppBanner />
          <QueryClientProvider client={queryClient}>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AuthProvider>
              <SentryProvider>
                <ToastProvider>
                <SidebarProvider>
                    <Routes>
                      {/* Root Route - Redirect to login or dashboard */}
                      <Route path="/" element={<RootRedirect />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/edit/:token" element={<EditLead />} />
                      <Route
                        path="/pre-confirmation-edit/:token"
                        element={<PreConfirmationEdit />}
                      />
                      <Route
                        path="/call-booking"
                        element={<CallBookingPage />}
                      />

                      {/* Quote Link Routes - Public access (for admin viewing customer quote links) */}
                      <Route
                        path="/quote/:tokenId"
                        element={
                          <QuoteLinkProvider>
                            <QuoteLinkPage />
                          </QuoteLinkProvider>
                        }
                      />
                      <Route
                        path="/quote/:tokenId/payment"
                        element={
                          <QuoteLinkProvider>
                            <PaymentInstructionsPage />
                          </QuoteLinkProvider>
                        }
                      />

                      {/* Protected Routes */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["reports:dashboard"]}
                          >
                            <Layout>
                              <DashboardProvider>
                                <DashboardPage />
                              </DashboardProvider>
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/ask-data"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["reports:analytics"]}
                          >
                            <Layout>
                              <DataCopilotPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/leads"
                        element={
                          <ProtectedRoute requiredPermissions={["leads:view"]}>
                            <Layout>
                              <Leads />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/leads/new"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["leads:create"]}
                          >
                            <Layout>
                              <ManualLeadForm />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/leads/intake"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["leads:create"]}
                          >
                            <Layout>
                              <AiLeadIntakeView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/leads/intake/:draftId"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["leads:create"]}
                          >
                            <Layout>
                              <AiLeadIntakeView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/leads/:leadId"
                        element={
                          <ProtectedRoute requiredPermissions={["leads:view"]}>
                            <Layout>
                              <LeadDetailsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/opportunities"
                        element={
                          <ProtectedRoute requiredPermissions={["leads:view"]}>
                            <Layout>
                              <OpportunitiesView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/opportunities/:opportunityId"
                        element={
                          <ProtectedRoute requiredPermissions={["leads:view"]}>
                            <Layout>
                              <OpportunityDetailView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/conversations"
                        element={
                          <ProtectedRoute requiredPermissions={["leads:view"]}>
                            <Layout>
                              <ConversationsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/quotes"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:view"]}>
                            <Layout>
                              <Quotes />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/quotes/new"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["quotes:create"]}
                          >
                            <Layout>
                              <QuoteForm />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/quotes/:quoteId"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:view"]}>
                            <Layout>
                              <QuoteDetailsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/bookings/:orderId"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:view"]}>
                            <Layout>
                              <BookingDetailsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/quotes/:quoteId/edit"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["quotes:update"]}
                          >
                            <Layout>
                              <QuoteEditView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/invoices"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:view"]}>
                            <Layout>
                              <InvoicesView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/invoices/new"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["quotes:create"]}
                          >
                            <Layout>
                              <InvoiceForm />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/invoices/:invoiceId/edit"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["quotes:update"]}
                          >
                            <Layout>
                              <InvoiceForm />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/invoices/:invoiceId"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:view"]}>
                            <Layout>
                              <InvoiceDetailsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/finance"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:view"]}>
                            <Layout>
                              <FinanceHubView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/finance/compensation/due"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:view"]}>
                            <Layout>
                              <DueCompensationView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/finance/invoices/:invoiceId/ledger"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:view"]}>
                            <Layout>
                              <InvoiceLedgerView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/finance/jobs/:jobId/ledger"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <Layout>
                              <JobLedgerView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/ledger"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:view"]}>
                            <Navigate to="/finance" replace />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/ledger/:jobId"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <LegacyJobLedgerRedirect />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/finance/reconciliation"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:view"]}>
                            <Layout>
                              <ReconciliationQueueView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/finance/ledger-reconciliation"
                        element={
                          <ProtectedRoute requiredPermissions={["finance:audit"]}>
                            <Layout>
                              <LedgerReconciliationAuditView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/finance/payouts/history"
                        element={
                          <ProtectedRoute requiredPermissions={["payouts:view"]}>
                            <Layout>
                              <PayoutHistoryView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/finance/payouts/run/:payoutRunId"
                        element={
                          <ProtectedRoute requiredPermissions={["payouts:view"]}>
                            <Layout>
                              <PayoutDetailView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/finance/payouts/provider/:providerId"
                        element={
                          <ProtectedRoute requiredPermissions={["payouts:view"]}>
                            <Layout>
                              <PayoutDashboardView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/followups"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["followups:view"]}
                          >
                            <Layout>
                              <FollowUpsPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/followups/new"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["followups:create"]}
                          >
                            <Layout>
                              <FollowUpCreatePage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/followups/:id/edit"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["followups:update"]}
                          >
                            <Layout>
                              <FollowUpEditPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/followups/:id"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["followups:view"]}
                          >
                            <Layout>
                              <FollowUpDetailView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/customers"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["customers:view"]}
                          >
                            <Layout>
                              <CustomersPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/customers/:customerId"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["customers:view"]}
                          >
                            <Layout>
                              <CustomerDetailView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/call-requests"
                        element={
                          <ProtectedRoute requiredPermissions={["call:view"]}>
                            <Layout>
                              <CallQueuePage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/orders"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <Layout>
                              <OrdersPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/orders/:orderId/edit"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:update"]}>
                            <Layout>
                              <OrderEditView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/orders/:orderId/incidents/:incidentId"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <Layout>
                              <IncidentDetailsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/orders/:orderId/incidents"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <Layout>
                              <IncidentsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/orders/:orderId/compensation"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <Layout>
                              <OrderCompensationView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/orders/:orderId"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <Layout>
                              <OrderDetailsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/trips"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <Layout>
                              <TripDashboard />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/trips/route-corridors"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <Layout>
                              <RouteCorridorsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/trips/:tripId"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <Layout>
                              <TripDetailsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/trips/:tripId/drops"
                        element={
                          <ProtectedRoute requiredPermissions={["orders:view"]}>
                            <Layout>
                              <DropDetailsView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Messages/chat route disabled - WhatsApp chatbot functionality paused */}
                      {/* <Route
                        path="/messages"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["messages:view"]}
                          >
                            <Layout>
                              <Messages />
                            </Layout>
                          </ProtectedRoute>
                        }
                      /> */}

                      {/* Operations Hub - Partners, Drivers, Products */}
                      <Route
                        path="/operations"
                        element={
                          <ProtectedRoute requiredPermissions={["agents:view"]}>
                            <Layout>
                              <OperationsLayout />
                            </Layout>
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<Navigate to="partners" replace />} />
                        <Route path="partners" element={<PartnersView />} />
                        <Route path="partners/:partnerId" element={<PartnerDetailView />} />
                        <Route path="drivers" element={<DriversView />} />
                        <Route path="products" element={<ProductsPage />} />
                      </Route>

                      {/* Backward compatible redirects */}
                      <Route
                        path="/partners"
                        element={
                          <ProtectedRoute requiredPermissions={["agents:view"]}>
                            <Navigate to="/operations/partners" replace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/partners/:partnerId"
                        element={
                          <ProtectedRoute requiredPermissions={["agents:view"]}>
                            <PartnerDetailRedirect />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/drivers"
                        element={
                          <ProtectedRoute requiredPermissions={["agents:view"]}>
                            <Navigate to="/operations/drivers" replace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/products"
                        element={
                          <ProtectedRoute requiredPermissions={["agents:view"]}>
                            <Navigate to="/operations/products" replace />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/vehicles"
                        element={
                          <ProtectedRoute requiredPermissions={["agents:view"]}>
                            <Layout>
                              <VehiclesView />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/route-corridors"
                        element={
                          <Navigate to="/trips/route-corridors" replace />
                        }
                      />


                      <Route
                        path="/agent"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <AgentPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/feedback"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["feedback:view"]}
                          >
                            <Layout>
                              <FeedbackPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/trust-profiles"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <TrustProfileTestPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/notifications"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["notifications:view"]}
                          >
                            <Layout>
                              <Notifications />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/settings"
                        element={
                          <ProtectedRoute requiredPermissions={["system:settings"]}>
                            <Layout>
                              <Settings />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/pricing"
                        element={<Navigate to="/pricing/rate-cards" replace />}
                      />

                      <Route
                        path="/pricing/rate-cards"
                        element={
                          <ProtectedRoute requiredPermissions={["quotes:pricing"]}>
                            <Layout>
                              <PricingRateCardsPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/settings/inventory-catalogue"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["quotes:pricing"]}
                          >
                            <Layout>
                              <InventoryCataloguePage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/settings/system-health"
                        element={
                          <RequireSuperAdmin>
                            <Layout>
                              <SystemHealthView />
                            </Layout>
                          </RequireSuperAdmin>
                        }
                      />

                      <Route
                        path="/terms-management"
                        element={
                          <RequireSuperAdmin>
                            <Layout>
                              <TermsManagementView />
                            </Layout>
                          </RequireSuperAdmin>
                        }
                      />

                      <Route
                        path="/users"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["users:view"]}
                          >
                            <Layout>
                              <UsersPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/campaign-urls"
                        element={
                          <ProtectedRoute
                            requiredPermissions={["system:settings"]}
                          >
                            <Layout>
                              <CampaignUrlGeneratorPage />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                </SidebarProvider>
                </ToastProvider>
              </SentryProvider>
            </AuthProvider>
          </Router>
        </QueryClientProvider>
        </ThemeProvider>
      </ConfigProvider>
    </ErrorBoundary>
  );
}

export default App;
